import type * as Sentry from '@sentry/angular';

type ReplayIntegrationOptions = NonNullable<Parameters<typeof Sentry.replayIntegration>[0]>;

/**
 * The only CSS selector session replay unmasks. See SENTRY_REPLAY_PRIVACY_OPTIONS.
 */
export const SENTRY_UNMASK_CLASS = 'sentry-unmask';

/**
 * Privacy settings for Sentry session replay.
 *
 * Replays record what is on screen: client names, phone numbers, emails,
 * addresses, appointment and client notes, and sales figures, including during
 * admin impersonation. All text, inputs and media stay masked so none of that
 * leaves the device.
 *
 * Reviewed decision (Stephen, 2026-09-28): text the app itself writes (button
 * captions, page titles, menu, segment and field labels) is unmasked through
 * the `sentry-unmask` class only, so support can see which screen and button a
 * replay is on. No tag or attribute selector is unmasked, because those
 * elements also render client data somewhere (e.g. `ion-title` shows staff
 * names and sale totals).
 *
 * How the SDK applies it (@sentry-internal/replay 8.55): an element matching
 * `unmask` shows its own text AND all its descendants' text, input values and
 * title/placeholder attributes, unless a nearer ancestor matches `mask`. So
 * the class may only sit on elements whose whole subtree is static app text.
 * `npm run check:sentry-unmask` (run in CI) enforces that on every template
 * under src/app, rejects the class anywhere else under src/ (index.html,
 * main.ts), and reads the class name from SENTRY_UNMASK_CLASS below.
 * `node scripts/check-sentry-unmask.mjs --write` tags new static elements.
 *
 * Do not set the flags below to false, widen `unmask`, or add `unblock`
 * selectors without a new reviewed decision; the spec next to this file pins
 * them.
 */
export const SENTRY_REPLAY_PRIVACY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  unmask: [`.${SENTRY_UNMASK_CLASS}`],
} as const satisfies ReplayIntegrationOptions;
